import * as React from "react";

/**
 * useAnimatedNumber — smoothly interpolate to a target value.
 *
 * Honours `prefers-reduced-motion` by snapping to the target immediately.
 * Uses requestAnimationFrame and an ease-out curve (~350ms).
 *
 * <AnimatedNumber value={unread} format={(v) => v} />
 * <AnimatedNumber value={cents/100} format={(v) => v.toFixed(2)} />
 */
export function useAnimatedNumber(target, { durationMs = 350 } = {}) {
  const [value, setValue] = React.useState(target ?? 0);
  const fromRef = React.useRef(target ?? 0);
  const startRef = React.useRef(0);
  const rafRef = React.useRef(0);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || target == null) { setValue(target ?? 0); fromRef.current = target ?? 0; return; }
    fromRef.current = value;
    startRef.current = performance.now();

    const tick = (now) => {
      const t = Math.min(1, (now - startRef.current) / durationMs);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      const v = fromRef.current + (target - fromRef.current) * eased;
      setValue(v);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setValue(target);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, durationMs]);

  return value;
}

/**
 * AnimatedNumber — display component that renders a smoothly-interpolated
 * number, formatted by the caller.
 */
export function AnimatedNumber({
  value,
  format = (v) => Math.round(v).toString(),
  className = "",
  testid = "animated-number",
}) {
  const v = useAnimatedNumber(value ?? 0);
  return (
    <span className={className} data-testid={testid}>{format(v)}</span>
  );
}
